import { Link, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type MeResponse } from '../api/client'
import { titleCards } from './titles'
import { deskHref } from './links'

const chip = 'inline-flex h-6 items-center rounded-full px-2.5 text-[12px] font-semibold whitespace-nowrap bg-ink-600 text-paper-dim'

/** /editorial: pick a title. Counts arrive with E2. */
export function TitleChooser() {
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  if (!me) return null
  const cards = titleCards(me.sections ?? [], me.role === 'admin')
  // An editor with exactly one title skips straight to it (spec §4).
  if (me.role !== 'admin' && (me.sections ?? []).length === 1) return <Navigate to={deskHref(me.sections![0].path)} replace />

  return (
    <div className="mx-auto flex max-w-[1180px] flex-col gap-8 px-4 pb-16 pt-12 md:px-10">
      <div className="flex max-w-[720px] flex-col gap-2">
        <h1 className="font-serif text-[48px] font-medium leading-[1.05] tracking-tight">Which title are you working on?</h1>
        <p className="text-[16px] text-paper-dim">Each title has its own desk: ideas, pieces in progress, the release schedule, and everything live on its free section and in its member archive.</p>
      </div>
      <ul className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(300px,100%),1fr))]">
        {cards.map((t) => (
          <li key={t.slug} className="flex">
            {t.state === 'open' ? (
              <Link
                to={deskHref(t.path)}
                className="flex w-full flex-col gap-4 rounded-[14px] border border-ink-500 bg-ink-700 p-6 text-paper shadow-sm hover:border-ink-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow"
              >
                <span className="flex flex-col gap-1">
                  <span className="font-serif text-[30px] font-medium leading-[1.1]">{t.title}</span>
                  <span className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className="inline-flex h-6 items-center rounded-full bg-radar-cyan/15 px-2.5 text-[12px] font-semibold text-radar-cyan">Free: {t.section}</span>
                    {t.members && <span className="inline-flex h-6 items-center rounded-full bg-radar-violet/15 px-2.5 text-[12px] font-semibold text-radar-violet">Members: {t.members}</span>}
                    {t.switchedOn === false && <span className={chip}>Not switched on</span>}
                  </span>
                </span>
                <span className="font-semibold text-amber-glow">{t.switchedOn === false ? 'Open the desk (read only) →' : 'Open the desk →'}</span>
              </Link>
            ) : (
              <div className="flex w-full flex-col gap-4 rounded-[14px] border border-dashed border-ink-400 bg-ink-800 p-6 text-paper-dim">
                <span className="flex flex-col gap-1">
                  <span className="font-serif text-[30px] font-medium leading-[1.1] text-paper-dim">{t.title}</span>
                  <span className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className={chip}>Free: {t.section}</span>
                    <span className={chip}>Not set up yet</span>
                  </span>
                </span>
                <span className="border-t border-ink-500 pt-3.5 text-[13px]">The writing agent does not know {t.section} yet: its voice, its readers and where its ideas come from. An administrator sets that up before pieces can be written here.</span>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
